import { formatHotelDate } from '../../../utils/dateUtils';
import {
  buildGuestRegistrationCardDocument,
  fitGuestRegistrationCardToPage,
} from './guestRegistrationCardHtml';

const PDF_PAGE_WIDTH_PX = 794;
const PDF_PAGE_HEIGHT_PX = 1123;

function waitForImages(doc) {
  const images = Array.from(doc.images || []);
  return Promise.all(images.map((img) => (
    img.complete ? Promise.resolve() : new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve;
    })
  )));
}

function grcFileName(data) {
  const code = String(data?.grCardNo || data?.guestName || 'GRC')
    .replace(/[^\w.-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return `GRC-${code || 'card'}.pdf`;
}

function withPrintedOn(data) {
  return { ...data, printedOn: formatHotelDate(new Date()) };
}

function waitForPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

function removePrintFrame(iframe) {
  if (iframe?.parentNode) iframe.parentNode.removeChild(iframe);
}

export function printGuestRegistrationCard(data) {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('title', 'Print Guest Registration Card');
  iframe.style.cssText = [
    'position:fixed',
    'right:0',
    'bottom:0',
    'width:210mm',
    'height:297mm',
    'border:0',
    'opacity:0',
    'pointer-events:none',
  ].join(';');
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;
  const frameDoc = iframe.contentDocument;
  if (!frameWindow || !frameDoc) {
    removePrintFrame(iframe);
    return;
  }

  frameDoc.open();
  frameDoc.write(buildGuestRegistrationCardDocument(withPrintedOn(data)));
  frameDoc.close();

  const triggerPrint = () => {
    fitGuestRegistrationCardToPage(frameDoc);
    try {
      frameWindow.focus();
      frameWindow.print();
    } finally {
      frameWindow.addEventListener('afterprint', () => removePrintFrame(iframe), { once: true });
      setTimeout(() => removePrintFrame(iframe), 60_000);
    }
  };

  waitForImages(frameDoc).then(() => {
    fitGuestRegistrationCardToPage(frameDoc);
    setTimeout(triggerPrint, 50);
  });
}

export async function downloadGuestRegistrationCardPdf(data) {
  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = [
    'position:fixed',
    'left:0',
    'top:0',
    `width:${PDF_PAGE_WIDTH_PX}px`,
    `height:${PDF_PAGE_HEIGHT_PX}px`,
    'border:0',
    'opacity:0.01',
    'pointer-events:none',
    'z-index:-1',
  ].join(';');
  document.body.appendChild(iframe);

  try {
    const frameDoc = iframe.contentDocument;
    frameDoc.open();
    frameDoc.write(buildGuestRegistrationCardDocument(withPrintedOn(data)));
    frameDoc.close();
    await waitForImages(frameDoc);
    await waitForPaint(frameDoc);

    const page = frameDoc.querySelector('.grc-page') || frameDoc.body;
    const sheet = frameDoc.querySelector('.grc-sheet');
    if (sheet) {
      sheet.style.transform = 'none';
      sheet.style.width = '100%';
      sheet.style.height = '100%';
    }

    const canvas = await html2canvas(page, {
      backgroundColor: '#fffdf8',
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      imageTimeout: 15000,
      letterRendering: true,
      width: PDF_PAGE_WIDTH_PX,
      height: PDF_PAGE_HEIGHT_PX,
      windowWidth: PDF_PAGE_WIDTH_PX,
      windowHeight: PDF_PAGE_HEIGHT_PX,
      onclone: (clonedDoc) => {
        const clonedSheet = clonedDoc.querySelector('.grc-sheet');
        if (clonedSheet) {
          clonedSheet.style.transform = 'none';
          clonedSheet.style.width = '100%';
          clonedSheet.style.height = '100%';
        }
        clonedDoc.querySelectorAll('.grc-page, .grc-sheet, .grc-stay-card, .grc-pill').forEach((el) => {
          el.style.boxShadow = 'none';
          el.style.backgroundImage = 'none';
        });
      },
    });

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    pdf.setFillColor(255, 253, 248);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pageWidth, pageHeight);
    pdf.save(grcFileName(data));
  } finally {
    iframe.remove();
  }
}
